// Sherikning O'Z kartalari: partiyalari, ulardagi kodlar va O'ZI
// ishlatgan kartalar ro'yxati.
//
// =====================================================================
// ⛔ BU FAYL YOZILGAN PAYTDA SERVERDA UCHTA ENDPOINTNING BIRORTASI HAM
//    YO'Q — O'LCHANGAN, TAXMIN QILINMAGAN (2026-08-24)
// =====================================================================
// `VoucherController` (`subscriber/internal/VoucherController.java:53`)
// `/api/vouchers` ostidagi YAGONA kontroller va unda `/my/` bilan
// boshlanadigan birorta yo'l yo'q. Ya'ni bugun uchalasi ham **404**
// qaytaradi.
//
// Shuning uchun bu qatlam 404 ni ALOHIDA holat sifatida beradi
// ({@link isEndpointMissing}) va ekran uni «ma'lumot yo'q» EMAS, «hali
// mavjud emas» deb chizadi. Farq bu kod bazasida qimmatga tushgan
// nuqson sinfi: bo'sh jadval sherikka «menda karta yo'q» deb o'qiladi
// va u administratorga «kartalarim yo'qolibdi» deb qo'ng'iroq qiladi.
// Ayni qaror `features/profile/profileApi.ts` da ham bor.
//
// =====================================================================
// ⛔ VAUCHER LEDGERDA YO'Q — bu yerda PUL HISOBI YOZILMAYDI
// =====================================================================
// Partiyaning `price` i qog'ozdagi NOMINAL, kassadan chiqqan pul emas:
// vaucher sotuvi jurnalga umuman tushmaydi (egasi qarori, 2026-08-24).
// Shuning uchun bu faylda hech qanday yig'indi, daromad yoki komissiya
// hisobi YO'Q va qo'shilmaydi — narx faqat kartaning ustiga chop
// etiladigan qiymat sifatida o'qiladi.
//
// ⚠️ `features/*` bir-birini import qilmaydi (`07-FRONTEND.md` §6):
// {@link isEndpointMissing} `features/profile` dagi ayni nomli
// funksiyaning TAKRORI. Nusxa ataylab — ikkalasi ham `shared/` ga
// chiqarilishi mumkin edi, lekin u paytda `features/profile` ning
// xato-tarjima qarori bu modulga ham bog'lanib qolardi.

import axios from 'axios'
import { z } from 'zod'
import { api } from '@/shared/api/client'
import { instantSchema, moneySchema, toMoney, uuidSchema } from '@/shared/api/schemas'
import type { Money } from '@/shared/lib/money'

/**
 * Karta holatlari — `VoucherState` (`voucher/enums/VoucherState.java:21-36`).
 *
 * ⚠️ TARTIB EKRANDA KO'RINADI: sanoq ustunlari shu ketma-ketlikda
 * chiziladi. U kartaning UMR YO'LI: chop etildi → sotildi →
 * ishlatildi, so'ng ikki tugash holati.
 */
export const VOUCHER_STATES = ['NEW', 'SOLD', 'ACTIVATED', 'EXPIRED', 'VOIDED'] as const
export type VoucherState = (typeof VOUCHER_STATES)[number]

// ⚠️ «Kodi sir bo'lgan holatlar» ro'yxati BU YERDA EMAS,
// `voucherCode.ts` da — u fayl SOF MANTIQ bo'lib qolishi kerak
// (haqiqiy `import` siz), aks holda qorovul uni bevosita chaqira
// olmasdi (`tests/guards.mjs` §12 dagi `commissionRule.ts` bilan ayni
// sabab: bu yerda axios ham, zod ham bor).

// --- Partiya ----------------------------------------------------------------

const batchSchema = z.object({
  id: uuidSchema,
  /**
   * Partiya raqami — `VoucherBatch#batchNo` (`long`).
   *
   * ⚠️ Serverda MAVJUD (o'lchandi: `voucher/domain/VoucherBatch.java`
   * va `VoucherBatchView`), lekin bu endpoint hali yozilmagan — maydon
   * tushib qolsa BUTUN jadval yiqilmasin. `null` ekranda `id` ning
   * qisqartmasiga ALMASHTIRILMAYDI: qisqartma raqamga o'xshab turadi
   * va sherik uni operatorga aytib yuborardi.
   */
  batchNo: z.number().nullish(),
  note: z.string().nullish(),
  planId: uuidSchema,
  codePrefix: z.string().nullish(),
  codeLength: z.number(),
  quantity: z.number(),
  price: moneySchema,
  /**
   * Karta SOTILMASA qachon kuyadi.
   *
   * ⛔ `Voucher#expiresAt` BILAN CHALKASHTIRILMAYDI: bu partiyaning
   * javon muddati, u esa AKTIVLASHTIRILGANDAN keyingi amal muddati.
   * Ikkalasi bir ustunda ko'rsatilsa sherik sotib bo'lmaydigan kartani
   * yaroqli deb hisoblardi.
   */
  validUntil: instantSchema.nullish(),
  createdAt: instantSchema,
  /**
   * Holat → soni.
   *
   * ⚠️ `null` — server bermadi, «nol» EMAS. Nolga qaytarish partiyani
   * butunlay sotilmagandek ko'rsatardi (`admin` panelidagi
   * `voidableCount` bilan ayni qoida).
   */
  counts: z.record(z.enum(VOUCHER_STATES), z.number()).nullish(),
})

export interface VoucherBatchRow {
  id: string
  /** `null` — server raqam bermadi; `id` bilan ALMASHTIRILMAYDI */
  batchNo: number | null
  /** Operator izohi (filial nomi va h.k.), har kartaga chop etiladi */
  note: string | null
  planId: string
  codePrefix: string | null
  codeLength: number
  quantity: number
  /** Qog'ozdagi NOMINAL — kassadan chiqqan pul emas (fayl boshidagi izoh) */
  price: Money
  /** Partiyaning javon muddati — `Voucher.expiresAt` EMAS */
  validUntil: string | null
  createdAt: string
  /** `null` — server sanoqni bermadi, ya'ni son NOMA'LUM */
  counts: Partial<Record<VoucherState, number>> | null
}

function toBatch(raw: z.infer<typeof batchSchema>): VoucherBatchRow {
  return {
    id: raw.id,
    batchNo: raw.batchNo ?? null,
    note: raw.note ?? null,
    planId: raw.planId,
    codePrefix: raw.codePrefix ?? null,
    codeLength: raw.codeLength,
    quantity: raw.quantity,
    price: toMoney(raw.price),
    validUntil: raw.validUntil ?? null,
    createdAt: raw.createdAt,
    counts: raw.counts ?? null,
  }
}

const batchPageSchema = z.object({
  rows: z.array(batchSchema),
  total: z.number(),
  page: z.number(),
  size: z.number(),
})

export interface VoucherBatchPage {
  rows: VoucherBatchRow[]
  total: number
}

/** Bir so'rovdagi partiyalar soni — jadval bitta sahifada ko'rinadi */
export const BATCH_PAGE_SIZE = 50

/** Ekranda ham, hisobotda ham AYNI satr — `AwaitingEndpoint` shuni chizadi */
export const MY_BATCHES_ENDPOINT = 'GET /api/vouchers/my/batches'
export const MY_CODES_ENDPOINT = 'GET /api/vouchers/my/batches/{id}/codes'
export const MY_ACTIVATIONS_ENDPOINT = 'GET /api/vouchers/my/activations'

/**
 * Sherikka BERILGAN partiyalar.
 *
 * ⚠️ «Berilgan» — `voucher_batch.agent_id`, ya'ni partiya kimning
 * qo'lida. Bu AKTIVATSIYAGA TA'SIR QILMAYDI: kartani har qanday agent
 * yoki reseller faollashtira oladi (`voucher.activate` ikkala
 * `PartnerKind` to'plamida). Ya'ni bu ro'yxat «men nimani sota olaman»
 * degan savolga javob beradi, «men nimani ishlata olaman» ga emas.
 */
export async function fetchMyBatches(
  search: string,
  page: number,
): Promise<VoucherBatchPage> {
  const clean = search.trim()
  const response = await api.get<unknown>('/api/vouchers/my/batches', {
    params: {
      page,
      size: BATCH_PAGE_SIZE,
      // Bo'sh qidiruv UMUMAN yuborilmaydi: server uni bo'sh satr deb
      // qabul qilib, hech narsa topmasligi mumkin.
      ...(clean === '' ? {} : { search: clean }),
    },
  })
  const parsed = batchPageSchema.parse(response.data)
  return { rows: parsed.rows.map(toBatch), total: parsed.total }
}

// --- Kartalar ---------------------------------------------------------------

const voucherSchema = z.object({
  id: uuidSchema,
  batchId: uuidSchema,
  serialNo: z.number().nullish(),
  code: z.string(),
  state: z.enum(VOUCHER_STATES),
  subscriberId: uuidSchema.nullish(),
  grantId: uuidSchema.nullish(),
  soldAt: instantSchema.nullish(),
  activatedAt: instantSchema.nullish(),
  expiresAt: instantSchema.nullish(),
  /**
   * KIM ishlatgan / KIM sotgan.
   *
   * =================================================================
   * ⛔ BU TO'RT MAYDON BUGUN SERVERDA UMUMAN YO'Q — O'LCHANGAN
   * =================================================================
   * `subscriber.voucher` jadvalining ustunlari (`V5_002__voucher.sql`):
   * `id, tenant_id, batch_id, code, state, subscriber_id, grant_id,
   * sold_at, activated_at, expires_at, created_at, updated_at`.
   * Keyingi to'rtta migratsiya ham `sold_by`/`activated_by` qo'shmagan.
   *
   * ⛔ Bundan ham muhimi: ustunning YO'QLIGI — ONGLI QAROR.
   * `VoucherRepository` javadoc'i (sotuvchini kartaga yozish haqida)
   * aynan shunday deydi: «U alohida qaror talab qiladi (ustun,
   * migratsiya, hisobot) va bu yerda ATAYLAB qilinmadi». Sotuvchi
   * bugun partiyaning `agent_id` sidan O'QISH PAYTIDA chiqariladi.
   *
   * Ya'ni bu maydonlar kelguncha ular HAR DOIM bo'sh bo'ladi. Ekran
   * shuni «hech kim ishlatmagan» deb ko'rsatmasligi SHART —
   * {@link VoucherCard.activatedByName} izohiga qarang.
   */
  activatedBy: uuidSchema.nullish(),
  activatedByName: z.string().nullish(),
  soldBy: uuidSchema.nullish(),
  soldByName: z.string().nullish(),
})

export interface VoucherCard {
  id: string
  batchId: string
  /** Partiya ichidagi tartib raqami — kartada `#<batchNo>-<serialNo>`; `null` — eski backend bermadi */
  serialNo: number | null
  /** ⛔ TAQDIM ETUVCHIGA XIZMAT QILUVCHI SIR — {@link SECRET_STATES} */
  code: string
  state: VoucherState
  subscriberId: string | null
  soldAt: string | null
  activatedAt: string | null
  /** Aktivlashtirilgandan KEYINGI muddat — partiyaning `validUntil` i EMAS */
  expiresAt: string | null
  /**
   * Kartani ishlatgan odamning nomi.
   *
   * ⛔ `null` NING MA'NOSI HOLATGA BOG'LIQ va ekran ikkovini AJRATADI:
   *   · holat `ACTIVATED` EMAS  → kartani hali hech kim ishlatmagan,
   *     ya'ni savolning o'zi yo'q — bo'sh joy to'g'ri javob;
   *   · holat `ACTIVATED`       → karta ishlatilgan, LEKIN kim
   *     ishlatgani YOZILMAGAN (ustun hali yo'q — sxema izohi).
   * Ikkalasini bitta chiziqcha bilan ko'rsatish «hech kim ishlatmagan»
   * degan YOLG'ON xulosani ishlatilgan kartaga yopishtirardi.
   */
  activatedByName: string | null
  /** Kartani sotgan odam; `null` ning ma'nosi {@link activatedByName} dagidek */
  soldByName: string | null
}

function toVoucher(raw: z.infer<typeof voucherSchema>): VoucherCard {
  return {
    id: raw.id,
    batchId: raw.batchId,
    serialNo: raw.serialNo ?? null,
    code: raw.code,
    state: raw.state,
    subscriberId: raw.subscriberId ?? null,
    soldAt: raw.soldAt ?? null,
    activatedAt: raw.activatedAt ?? null,
    expiresAt: raw.expiresAt ?? null,
    activatedByName: raw.activatedByName ?? null,
    soldByName: raw.soldByName ?? null,
  }
}

/** Bitta so'rovda olinadigan kod soni — kontraktdagi sukut */
export const CODES_LIMIT = 1000

/**
 * Partiyadagi kartalar — KODLARNI OCHIB BERADI.
 *
 * ⛔ NATIJA KESHGA SAQLANMAYDI va bu ONGLI QAROR (admin panelidagi
 * `fetchVoucherCodes` bilan ayni sabab): kod pul qiymatiga teng
 * bearer-sir. `useQuery` bilan olinsa u `queryClient` da sahifa
 * yopilgandan keyin ham yashardi va DevTools'dan ko'rinardi. Chaqiruvchi
 * uni faqat oynaning LOKAL holatida tutadi va oyna yopilganda yo'qoladi.
 */
export async function fetchMyBatchCodes(batchId: string): Promise<VoucherCard[]> {
  const response = await api.get<unknown>(`/api/vouchers/my/batches/${batchId}/codes`, {
    params: { limit: CODES_LIMIT },
  })
  return z.array(voucherSchema).parse(response.data).map(toVoucher)
}

// --- Chop etish varag'i -----------------------------------------------------

const printSheetSchema = z.object({
  batchNo: z.number(),
  planId: uuidSchema,
  note: z.string().nullish(),
  quantity: z.number(),
  price: moneySchema,
  validUntil: instantSchema.nullish(),
  portalUrl: z.string().nullish(),
  cards: z.array(
    z.object({
      serialNo: z.number(),
      code: z.string(),
      password: z.string().nullish(),
      qrToken: z.string().nullish(),
    }),
  ),
  skipped: z.number(),
  branchName: z.string().nullish(),
  companyName: z.string().nullish(),
  logoUrl: z.string().nullish(),
  instructions: z.string().nullish(),
  credentialMode: z.enum(['CODE', 'LOGIN_PASSWORD']).nullish(),
  printQr: z.boolean().nullish(),
})

export interface VoucherPrintSheetData {
  batchNo: number
  planId: string
  note: string | null
  quantity: number
  price: Money
  validUntil: string | null
  /** `null` — muhitda portal manzili sozlanmagan, QR faqat kodni saqlaydi */
  portalUrl: string | null
  /**
   * `password` — faqat login va parolli kartada (`code` shunda login).
   * `qrToken` — QR uchun alohida token; `null` — eski karta, QR kodni o'zi saqlaydi.
   */
  cards: { serialNo: number; code: string; password: string | null; qrToken: string | null }[]
  /** Tanlovda bor, lekin ishlatilgan/kuygan/bekor qilingani uchun chiqmaydigan kartalar */
  skipped: number
  /** Partiya ushlovchisi turgan filial; `null` — filialga bog'lanmagan */
  branchName: string | null
  /** Kompaniya nomi (brend sozlamasi, bo'lmasa tashkilot nomi) — kartada chiqaruvchi qatorida */
  companyName: string | null
  /** Tashkilot logotipi (ochiq havola); `null` — yuklanmagan */
  logoUrl: string | null
  /** Partiya yoki tashkilot yo'riqnomasi; `null` — kartada umumiy maslahat chiqadi */
  instructions: string | null
  /** ADR-056: aralash kod yoki raqamli login va parol */
  credentialMode: 'CODE' | 'LOGIN_PASSWORD'
  /** `false` — partiya QR'siz chop etiladi */
  printQr: boolean
}

export type PrintSelection = { from: number; to: number } | { serials: readonly number[] }

/**
 * O'z partiyasining chop etish varag'i — KODLARNI ochadi, har so'rov
 * serverda `VOUCHER_BATCH_PRINT` auditini qoldiradi. Keshga saqlanmaydi.
 */
export async function fetchMyPrintSheet(batchId: string, selection: PrintSelection): Promise<VoucherPrintSheetData> {
  const params = new URLSearchParams()
  if ('serials' in selection) selection.serials.forEach((serial) => params.append('serial', String(serial)))
  else {
    params.set('from', String(selection.from))
    params.set('to', String(selection.to))
  }
  const response = await api.get<unknown>(`/api/vouchers/my/batches/${batchId}/print`, { params })
  const raw = printSheetSchema.parse(response.data)
  return {
    batchNo: raw.batchNo,
    planId: raw.planId,
    note: raw.note ?? null,
    quantity: raw.quantity,
    price: toMoney(raw.price),
    validUntil: raw.validUntil ?? null,
    portalUrl: raw.portalUrl ?? null,
    cards: raw.cards.map((card) => ({
      serialNo: card.serialNo,
      code: card.code,
      password: card.password ?? null,
      qrToken: card.qrToken ?? null,
    })),
    skipped: raw.skipped,
    branchName: raw.branchName ?? null,
    companyName: raw.companyName ?? null,
    logoUrl: raw.logoUrl ?? null,
    instructions: raw.instructions ?? null,
    credentialMode: raw.credentialMode ?? 'CODE',
    printQr: raw.printQr ?? true,
  }
}

// --- Men ishlatganlarim -----------------------------------------------------

const activationSchema = z.object({
  voucherId: uuidSchema,
  code: z.string(),
  batchNo: z.number().nullish(),
  planId: uuidSchema.nullish(),
  subscriberLogin: z.string().nullish(),
  activatedAt: instantSchema,
  expiresAt: instantSchema.nullish(),
})

const activationPageSchema = z.object({
  rows: z.array(activationSchema),
  total: z.number(),
  page: z.number(),
  size: z.number(),
})

export interface VoucherActivationRow {
  voucherId: string
  /**
   * ⚠️ BU RO'YXATDA KOD BERKITILMAYDI: har qatori ALLAQACHON
   * ishlatilgan kartaga tegishli ({@link SECRET_STATES} izohi). Uni
   * yashirish nizoni hal qilishning yagona yo'lini yopardi.
   */
  code: string
  batchNo: number | null
  planId: string | null
  subscriberLogin: string | null
  activatedAt: string
  expiresAt: string | null
}

export interface VoucherActivationPage {
  rows: VoucherActivationRow[]
  total: number
}

export const ACTIVATION_PAGE_SIZE = 50

/**
 * «Men ishlatgan kartalar» — davr bo'yicha.
 *
 * ⚠️ `from`/`to` UTC ONLARI, kalendar kunlari EMAS. Ularni tenant
 * zonasida hisoblash chaqiruvchining ishi (`tenantRangeWindow`):
 * Kobul tenantida (UTC+04:30) brauzer zonasidan hisoblangan chegara
 * kuniga yarim soat davomida oraliqni bir kunga surib yuborardi —
 * JIMGINA, chunki so'rov baribir 200 qaytaradi.
 */
export async function fetchMyActivations(
  window: { from: string; to: string },
  page: number,
): Promise<VoucherActivationPage> {
  const response = await api.get<unknown>('/api/vouchers/my/activations', {
    params: { from: window.from, to: window.to, page, size: ACTIVATION_PAGE_SIZE },
  })
  const parsed = activationPageSchema.parse(response.data)
  return {
    rows: parsed.rows.map((raw) => ({
      voucherId: raw.voucherId,
      code: raw.code,
      batchNo: raw.batchNo ?? null,
      planId: raw.planId ?? null,
      subscriberLogin: raw.subscriberLogin ?? null,
      activatedAt: raw.activatedAt,
      expiresAt: raw.expiresAt ?? null,
    })),
    total: parsed.total,
  }
}

/**
 * Xato «endpoint hali yozilmagan» degani ekanini aniqlaydi.
 *
 * ⚠️ `404` bu yerda ikki narsani anglatishi mumkin: mapping yo'q yoki
 * partiya sherikning doirasidan tashqarida. Ikkalasida ham to'g'ri xulq
 * bir xil — BO'SH JADVAL EMAS, ochiq aytilgan bo'shliq.
 *
 * ⚠️ `403` HAM SHU YERGA KIRADI va bu o'lchangan qaror. `voucher.stock.read`
 * huquqining O'ZI endi `PartnerKind` ning ikkala to'plamida ham bor
 * (`:63`, `:125`), lekin endpoint hali yozilmagan — u yozilganda esa
 * DOIRA (`OWN`/`SUBTREE`) qanday majburlanishi hali noma'lum. «Ruxsat
 * yo'q» degan qizil xato bu ikkala holatda ham noto'g'ri bo'lardi: panel
 * ham, sherik ham hech narsa buzmagan.
 */
export function isEndpointMissing(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false
  const status = error.response?.status
  return status === 404 || status === 403
}
